import { useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, LogOut } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Avatar, Badge } from '@/components/ui';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/utils/cn';

/** Mirrors TopNav.tsx for the platform-admin identity — no org Settings/Help
 * links (they don't apply here), and shows the admin's role as a badge. */
export function PlatformTopNav() {
  const navigate = useNavigate();
  const { platformAdmin, logout } = useAuthStore();
  const displayName = platformAdmin?.display_name || platformAdmin?.email || 'Admin';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-border bg-surface px-4 md:px-6">
      <div className="flex items-center gap-2">
        {platformAdmin?.role === 'super_admin' && <Badge color="blue">Super Admin</Badge>}
        {platformAdmin?.role === 'co_admin' && <Badge color="gray">Co-Admin</Badge>}
        {platformAdmin?.role === 'viewer' && <Badge color="yellow">Viewer</Badge>}
      </div>

      <div className="ml-auto flex items-center gap-3">
        <button
          type="button"
          aria-label="Notifications"
          className="rounded-full p-2 text-text-secondary hover:bg-surface-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
        </button>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            className={cn(
              'flex items-center gap-2 rounded p-1.5 text-sm font-medium text-text-primary hover:bg-surface-secondary',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
            )}
          >
            <Avatar name={displayName} size="sm" />
            <span className="hidden md:inline">{displayName}</span>
            <ChevronDown className="h-4 w-4 text-text-secondary" aria-hidden="true" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 min-w-[180px] rounded border border-border bg-white p-1 shadow-dropdown"
            >
              <DropdownMenu.Item
                onSelect={handleLogout}
                className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-danger outline-none data-[highlighted]:bg-danger-light"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" /> Sign Out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
